import React, { useEffect, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import { useLocation, useNavigate } from 'react-router-dom';
import { agreeTerms, getTermsStatus } from '../api/terms';
import styles from './TermsReagreementPage.module.css';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '../utils/apiError';

const TermsReagreementPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('termsReagreement');
  const { t: tApi } = useTranslation('apiErrors');

  const [missingDocs, setMissingDocs] = useState([]);
  const [checked, setChecked] = useState({});
  const [viewedDocIds, setViewedDocIds] = useState({});
  const [previewDoc, setPreviewDoc] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const nextPath = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get('next');
    try {
      if (raw) {
        const url = new URL(raw, window.location.origin);
        if (url.origin === window.location.origin) return raw;
      }
    } catch {
      // 無効なURLはデフォルトにフォールバック
    }
    return '/profile/my';
  }, [location.search]);

  useEffect(() => {
    const load = async () => {
      try {
        const status = await getTermsStatus();
        const missing = Array.isArray(status?.missing) ? status.missing : [];
        setMissingDocs(missing);

        if (!status?.requires_reagreement || missing.length === 0) {
          navigate(nextPath, { replace: true });
        }
      } catch (e) {
        setError(t('errorLoad'));
      }
    };

    load();
  }, [navigate, nextPath]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const unchecked = missingDocs.find((doc) => !checked[doc.terms_version_id]);
    if (unchecked) {
      setError(t('errorUnchecked'));
      return;
    }

    setSubmitting(true);
    try {
      await agreeTerms(
        missingDocs.map((doc) => ({ terms_version_id: doc.terms_version_id }))
      );
      setSuccess(t('successSaved'));
      navigate(nextPath, { replace: true });
    } catch (e) {
      const message = getApiErrorMessage(e, tApi);
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>{t('title')}</h2>
      <p className={styles.description}>{t('description')}</p>

      {error && <p className={styles.error}>{error}</p>}
      {success && <p className={styles.success}>{success}</p>}

      <form onSubmit={handleSubmit}>
        <ul className={styles.list}>
          {missingDocs.map((doc) => (
            <li key={doc.terms_version_id} className={styles.item}>
              <label className={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={!!checked[doc.terms_version_id]}
                  disabled={!viewedDocIds[doc.terms_version_id]}
                  onChange={(e) => {
                    setChecked((prev) => ({
                      ...prev,
                      [doc.terms_version_id]: e.target.checked,
                    }));
                  }}
                />
                <span>
                  <button
                    type="button"
                    className={styles.linkButton}
                    onClick={() => {
                      setViewedDocIds((prev) => ({ ...prev, [doc.terms_version_id]: true }));
                      setPreviewDoc(doc);
                    }}
                    disabled={submitting}
                  >
                    {t('termsTitle.' + doc.type, { defaultValue: doc.title })}
                  </button>
                  {' '}{t('agreeLabel')}
                </span>
              </label>
            </li>
          ))}
        </ul>
        {missingDocs.length > 0 && (
          <p className={styles.hint}>{t('hint')}</p>
        )}

        <button type="submit" className={styles.button} disabled={submitting || missingDocs.length === 0}>
          {submitting ? t('btnSubmitting') : t('btnSubmit')}
        </button>
      </form>

      {previewDoc && (
        <div
          role="dialog"
          aria-modal="true"
          className={styles.modalOverlay}
          onClick={() => setPreviewDoc(null)}
        >
          <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>{t('termsTitle.' + previewDoc.type, { defaultValue: previewDoc.title })}</h3>
            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewDoc.content || '') }} />
            <div className={styles.modalFooter}>
              <button type="button" className={styles.closeButton} onClick={() => setPreviewDoc(null)}>
                {t('btnClose')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TermsReagreementPage;
